import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Search, UserPlus, Eye, CheckCircle, XCircle, Clock, CreditCard,
  Image as ImageIcon, Trash2, Calendar, Phone, Mail,
  AlertTriangle, History, Key
} from "lucide-react";

const Suscripciones = () => {
  const [subs, setSubs] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [editDialog, setEditDialog] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, planesRes] = await Promise.all([
        axios.get(`${API}/suscripciones`),
        axios.get(`${API}/planes`)
      ]);
      setSubs(subsRes.data);
      setPlanes(planesRes.data);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdate = async () => {
    if (!editDialog) return;
    try {
      await axios.put(`${API}/suscripciones/${editDialog.suscripcion_id}`, editData);
      toast.success("Suscripcion actualizada correctamente");
      setEditDialog(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleQuickAction = async (sub, estatus) => {
    try {
      await axios.put(`${API}/suscripciones/${sub.suscripcion_id}`, { estatus });
      toast.success(`Suscripcion ${estatus === "aprobado" ? "aprobada" : estatus === "rejected" ? "rechazada" : estatus}`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar suscripcion?")) return;
    try {
      await axios.delete(`${API}/suscripciones/${id}`);
      toast.success("Eliminada");
      await fetchData();
    } catch { toast.error("Error"); }
  };

  const filtered = subs.filter(s => {
    const matchSearch = s.nombre?.toLowerCase().includes(search.toLowerCase()) || s.correo?.toLowerCase().includes(search.toLowerCase());
    const realState = s.estado_real || s.estatus;
    const matchFiltro = filtro === "todas" || realState === filtro || s.estatus === filtro;
    return matchSearch && matchFiltro;
  });

  const realStateConfig = {
    activa: { label: "Activa", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
    por_vencer: { label: "Por vencer", color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
    vencida: { label: "Vencida", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
    suspendida: { label: "Suspendida", color: "bg-stone-200 text-stone-700 border-stone-300", icon: XCircle },
    cancelada: { label: "Cancelada", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
    rechazada: { label: "Rechazada", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
    pendiente: { label: "Pendiente de pago", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  };

  const estatusConfig = {
    pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-800", icon: Clock },
    aprobado: { label: "Activo", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
    active: { label: "Activo", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
    rechazado: { label: "Rechazado", color: "bg-red-100 text-red-800", icon: XCircle },
    rejected: { label: "Rechazado", color: "bg-red-100 text-red-800", icon: XCircle },
    prueba: { label: "Prueba", color: "bg-blue-100 text-blue-800", icon: Calendar },
    trial: { label: "Prueba", color: "bg-blue-100 text-blue-800", icon: Calendar },
    suspendido: { label: "Suspendido", color: "bg-stone-200 text-stone-600", icon: XCircle },
    suspended: { label: "Suspendido", color: "bg-stone-200 text-stone-600", icon: XCircle },
    expired: { label: "Expirado", color: "bg-red-100 text-red-700", icon: AlertTriangle },
  };

  const countByRealState = (state) => subs.filter(s => (s.estado_real || s.estatus) === state).length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div>
          <h1 className="font-display text-xl sm:text-2xl lg:text-3xl text-stone-900" data-testid="suscripciones-title">Suscripciones</h1>
          <p className="text-xs sm:text-sm text-stone-500">Gestion de usuarios y planes ({subs.length} total)</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full">{countByRealState("activa")} activas</span>
          <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full">{countByRealState("por_vencer")} por vencer</span>
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full">{countByRealState("vencida")} vencidas</span>
          <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full">{countByRealState("pendiente")} pendientes</span>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Buscar por nombre o correo..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 border-stone-300" data-testid="search-suscripciones" />
        </div>
        <div className="overflow-x-auto -mx-1 px-1">
          <div className="flex gap-2 min-w-max">
            {["todas", "activa", "por_vencer", "vencida", "pendiente", "suspendida", "cancelada"].map(f => (
              <Button key={f} size="sm" variant={filtro === f ? "default" : "outline"}
                onClick={() => setFiltro(f)}
                className={`text-xs whitespace-nowrap ${filtro === f ? "btn-primary" : "border-stone-300"}`}
                data-testid={`filtro-${f}`}
              >
                {f === "todas" ? "Todas" : (realStateConfig[f]?.label || f)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <UserPlus className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-stone-300 mb-3" />
          <h3 className="text-base sm:text-lg font-medium text-stone-700">No hay suscripciones</h3>
          <p className="text-xs sm:text-sm text-stone-500">Los usuarios se registraran desde la pagina web</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-4">
          {filtered.map(sub => {
            const realState = sub.estado_real || sub.estatus;
            const cfg = realStateConfig[realState] || estatusConfig[sub.estatus] || estatusConfig.pendiente;
            const Icon = cfg.icon;
            const diasRest = sub.dias_restantes;
            return (
              <Card key={sub.suscripcion_id} className="border-stone-200 overflow-hidden" data-testid={`sub-${sub.suscripcion_id}`}>
                <CardContent className="p-3 sm:p-4">
                  {/* Name + status */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-medium text-stone-900 text-sm sm:text-base">{sub.nombre}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 border ${cfg.color}`} data-testid={`sub-status-${sub.suscripcion_id}`}>
                      <Icon className="w-3 h-3" /> {cfg.label}
                    </span>
                    {diasRest !== null && diasRest !== undefined && realState !== "cancelada" && realState !== "suspendida" && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${diasRest < 0 ? "bg-red-50 text-red-700" : diasRest <= 7 ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-600"}`}>
                        {diasRest < 0 ? `Vencida hace ${Math.abs(diasRest)}d` : diasRest === 0 ? "Vence hoy" : `${diasRest}d restantes`}
                      </span>
                    )}
                  </div>

                  {/* Plan */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1">
                      <CreditCard className="w-3 h-3" />{sub.plan_nombre} - ${sub.plan_precio?.toLocaleString()}
                    </span>
                    {sub.usuario_creado && (
                      <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1">
                        <Key className="w-3 h-3" />Cuenta creada
                      </span>
                    )}
                  </div>

                  {/* Contact info */}
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-x-4 text-xs sm:text-sm text-stone-600 mb-2">
                    <span className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 shrink-0" /> {sub.telefono}</span>
                    <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" /> {sub.correo}</span>
                  </div>

                  {/* Comprobante */}
                  {sub.comprobante_url && (
                    <a href={`${BACKEND_URL}${sub.comprobante_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mb-2" data-testid={`ver-comprobante-${sub.suscripcion_id}`}>
                      <ImageIcon className="w-3.5 h-3.5" /> Ver comprobante
                    </a>
                  )}

                  {/* Dates */}
                  <div className="flex flex-wrap gap-2 sm:gap-4 text-xs text-stone-400 mb-3">
                    {sub.fecha_inicio && <span>Inicio: {new Date(sub.fecha_inicio).toLocaleDateString("es-MX")}</span>}
                    {sub.fecha_fin && <span>Fin: {new Date(sub.fecha_fin).toLocaleDateString("es-MX")}</span>}
                    {sub.dias_prueba > 0 && <span className="text-blue-600">{sub.dias_prueba} dias de prueba</span>}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
                    <Button size="sm" variant="outline" className="border-stone-300 text-xs flex-1 sm:flex-none"
                      onClick={() => { setEditDialog(sub); setEditData({ estatus: sub.estatus, dias_prueba: sub.dias_prueba || 0, notas_admin: sub.notas_admin || "", plan_id: sub.plan_id }); }}
                      data-testid={`btn-gestionar-${sub.suscripcion_id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> Gestionar
                    </Button>
                    {sub.estatus === "pendiente" && (
                      <>
                        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs flex-1 sm:flex-none"
                          onClick={() => handleQuickAction(sub, "aprobado")}
                          data-testid={`btn-aprobar-${sub.suscripcion_id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Aprobar
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 text-xs flex-1 sm:flex-none"
                          onClick={() => handleQuickAction(sub, "rejected")}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                        </Button>
                      </>
                    )}
                    {(realState === "vencida" || realState === "por_vencer") && (
                      <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
                        onClick={() => { setEditDialog(sub); setEditData({ estatus: "aprobado", dias_prueba: 30, notas_admin: sub.notas_admin || "", plan_id: sub.plan_id }); }}
                        data-testid={`btn-renovar-${sub.suscripcion_id}`}
                      >
                        Renovar
                      </Button>
                    )}
                    {realState === "activa" && (
                      <Button size="sm" variant="outline" className="border-stone-300 text-xs"
                        onClick={() => handleQuickAction(sub, "suspendido")}
                        data-testid={`btn-suspender-${sub.suscripcion_id}`}
                      >
                        Suspender
                      </Button>
                    )}
                    {realState === "suspendida" && (
                      <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 text-xs"
                        onClick={() => handleQuickAction(sub, "aprobado")}
                        data-testid={`btn-reactivar-${sub.suscripcion_id}`}
                      >
                        Reactivar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                      onClick={() => handleDelete(sub.suscripcion_id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit/Manage Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-lg sm:text-xl">Gestionar Suscripcion</DialogTitle></DialogHeader>
          {editDialog && (
            <div className="space-y-4 py-2">
              <div className="bg-stone-50 p-3 rounded-sm space-y-1">
                <p className="font-medium text-sm sm:text-base">{editDialog.nombre}</p>
                <p className="text-xs sm:text-sm text-stone-600 break-all">{editDialog.correo} | {editDialog.telefono}</p>
                <p className="text-xs sm:text-sm text-emerald-700 font-medium">{editDialog.plan_nombre} - ${editDialog.plan_precio?.toLocaleString()}</p>
                {editDialog.tipo_negocio && <p className="text-xs text-stone-500">{editDialog.tipo_negocio}</p>}
              </div>

              {editDialog.comprobante_url && (
                <a href={`${BACKEND_URL}${editDialog.comprobante_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:underline bg-blue-50 p-2 rounded-sm">
                  <ImageIcon className="w-4 h-4 shrink-0" /> Ver comprobante de pago
                </a>
              )}

              <div className="space-y-2">
                <Label className="text-sm">Estatus</Label>
                <Select value={editData.estatus} onValueChange={(v) => setEditData(p => ({ ...p, estatus: v }))}>
                  <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="aprobado">Activo (Aprobado)</SelectItem>
                    <SelectItem value="prueba">Prueba gratuita</SelectItem>
                    <SelectItem value="suspended">Suspendido</SelectItem>
                    <SelectItem value="rejected">Rechazado</SelectItem>
                    <SelectItem value="expired">Expirado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Plan</Label>
                <Select value={editData.plan_id} onValueChange={(v) => setEditData(p => ({ ...p, plan_id: v }))}>
                  <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {planes.map(p => (
                      <SelectItem key={p.plan_id} value={p.plan_id}>{p.nombre} - ${p.precio}/{p.periodo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Dias de prueba</Label>
                <div className="flex gap-2">
                  {[7, 14, 30].map(d => (
                    <Button key={d} size="sm" variant={editData.dias_prueba === d ? "default" : "outline"}
                      onClick={() => setEditData(p => ({ ...p, dias_prueba: d, estatus: "prueba" }))}
                      className={`flex-1 ${editData.dias_prueba === d ? "btn-primary" : "border-stone-300"}`}
                      data-testid={`btn-trial-${d}`}
                    >{d} dias</Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Notas admin</Label>
                <Input value={editData.notas_admin} onChange={(e) => setEditData(p => ({ ...p, notas_admin: e.target.value }))} placeholder="Notas internas..." className="border-stone-300" />
              </div>

              {editDialog.historial && editDialog.historial.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1 text-sm"><History className="w-3.5 h-3.5" /> Historial</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {editDialog.historial.map((h, i) => (
                      <div key={h.fecha || `hist-${i}`} className="text-xs bg-stone-50 p-2 rounded-sm">
                        <div className="flex justify-between gap-2">
                          <span className="break-all">
                            <strong className="capitalize">{h.accion}</strong>
                            {h.admin && <span className="text-stone-400"> por {h.admin}</span>}
                          </span>
                          <span className="text-stone-400 shrink-0">
                            {new Date(h.fecha).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
                          </span>
                        </div>
                        {h.notas && <p className="text-stone-500 mt-0.5">{h.notas}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditDialog(null)} className="border-stone-300 w-full sm:w-auto">
              Cancelar
            </Button>
            <Button onClick={handleUpdate} className="btn-primary w-full sm:w-auto" data-testid="btn-guardar-suscripcion">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suscripciones;
